import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpResponse} from '@angular/common/http';
import {firstValueFrom, forkJoin, map, Observable, of, ReplaySubject, switchMap, tap, throwError} from 'rxjs';
import {buildPath} from '@utils/util-builder';
import {Contractee} from '@models/contractee.model';
import {Hotel as HotelDTO} from '@entities/hotel';
import {environment} from '@env/environment';
import {ApiResponse} from '@models/api.response';
import {EvidenceMonthResponse} from '@models/evidence.model';
import {EvidenceConfigResponse} from '@models/evidence-config.model';
import {ContracteeModule} from '@models/contractee-module.interface';
import {GenericSelect} from '@models/generic-select.interface';
import {SpringPage} from '@models/page.model';

/**
 * Creator: Fernando Tapia
 * Company: TAXSAT
 * Date: 8.02.22
 *
 * Class to manage all data information from Hotels
 * Copyright TAXSAT
 */
@Injectable({
    providedIn: 'root'
})
export class HotelService {
    private readonly _hotel: ReplaySubject<Contractee> = new ReplaySubject<Contractee>(1);
    private readonly baseUrl: string = 'data-core/Hotel';

    constructor(private readonly _http: HttpClient) {
    }

    set hotel(value: Contractee) {
        // Store the value
        this._hotel.next(value);
    }

    get hotel$(): Observable<Contractee> {
        return this._hotel.asObservable();
    }

    /**
     * Method to get all hotels by Users
     */
    getHotelsByUser(offset: number, pageNumber: number, pageSize: number): Observable<any> {
        return this._http.get<any>(buildPath(
            ['data-core/Hotel/getHotels?offset=' + offset + '&pageNumber=' + pageNumber + '&pageSize=' +
            pageSize]));
    }

    getContracteesNamesForSelector(): Observable<GenericSelect[]> {
        const url: string = buildPath([this.baseUrl, 'all-names']);
        return this._http.get<ApiResponse<GenericSelect[]>>(url).pipe(
            map((response: ApiResponse<GenericSelect[]>): GenericSelect[] => response.data)
        );
    }

    getContracteesNamesWithBailsForSelector(): Observable<GenericSelect[]> {
        const url: string = buildPath([this.baseUrl, 'all-names-fianza']);
        return this._http.get<ApiResponse<GenericSelect[]>>(url).pipe(
            map((response: ApiResponse<GenericSelect[]>): GenericSelect[] => response.data)
        );
    }

    getCurrent(): Observable<HotelDTO> {
        const url: string = buildPath([this.baseUrl, 'current']);
        return this._http.get<ApiResponse<HotelDTO>>(url).pipe(
            map((response: ApiResponse<HotelDTO>): HotelDTO => response.data)
        );
    }

    /** Single-page read on the canonical route; authorization remains server-side. */
    getMatrix(contracteeId: number, dateInit: string, dateEnd: string): Observable<SpringPage<any>> {
        if (!Number.isSafeInteger(contracteeId) || contracteeId <= 0) {
            return throwError(() => new Error('A valid contracteeId is required'));
        }
        return this._http.get<SpringPage<any>>(this.matrixUrl(contracteeId, dateInit, dateEnd, 0));
    }

    /** Binary reports use the same authorized backend read, never the cached browser rows. */
    getMatrixReport(contracteeId: number, inicio: string, fin: string, format: 'xlsx' | 'pdf'): Observable<HttpResponse<Blob>> {
        if (!Number.isSafeInteger(contracteeId) || contracteeId <= 0) {
            return throwError(() => new Error('A valid contracteeId is required'));
        }
        return this._http.get(buildPath([this.baseUrl, `getKPImatrix/${contracteeId}/export`]), {
            params: {inicio, fin, format}, responseType: 'blob', observe: 'response'
        });
    }

    private matrixUrl(contracteeId: number, dateInit: string, dateEnd: string, pageNumber: number): string {
        return buildPath([this.baseUrl, 'getKPImatrix/' + contracteeId + '?fin=' + dateEnd + '&inicio=' + dateInit
        + '&pageSize=100&pageNumber=' + pageNumber]);
    }

    /**
     * Fetches page 0 from the given URL builder, then, if the SpringPage response
     * reports more than one totalPages, requests the remaining pages in parallel
     * and combines all their content into a single array.
     */
    private fetchAllPages(buildUrl: (pageNumber: number) => string): Observable<any[]> {
        return this._http.get<any>(buildUrl(0)).pipe(
            switchMap((page: any) => {
                if (page.totalPages <= 1) {
                    return of(page.content);
                }

                const requests: Observable<any>[] = [];
                for (let i = 1; i < page.totalPages; i++) {
                    requests.push(this._http.get<any>(buildUrl(i)));
                }

                return forkJoin(requests).pipe(
                    map((pages: any[]): any[] => page.content.concat(...pages.map((p: any) => p.content)))
                );
            })
        );
    }

    /** Fetch all pages without publishing a partial dataset if any page fails. */
    getFullMatrix(contracteeId: number, dateInit: string, dateEnd: string): Observable<any[]> {
        if (!Number.isSafeInteger(contracteeId) || contracteeId <= 0) {
            return throwError(() => new Error('A valid contracteeId is required'));
        }
        return this.fetchAllPages((pageNumber: number) => this.matrixUrl(contracteeId, dateInit, dateEnd, pageNumber));
    }

    /**
     * Method to get hotel by id
     *
     * @param id
     */
    getHotel(id: any): Observable<any> {
        return this._http.get<any>(buildPath(
            ['data-core/Hotel/' + id])).pipe(
            tap((response: any) => {
                this._hotel.next(response);
            })
        );
    }

    /**
     * Method to get hotel by Id
     *
     * @param id
     */
    getHotelByIdInProvider(id: any): Observable<any> {
        return this._http.get<any>(buildPath(
            ['data-core/Hotel/' + id]));
    }

    /**
     * Method to update hotel information
     *
     * @param id
     * @param hotel
     */
    updateHotel(id: number, hotel: Contractee): Observable<any> {
        return this._http.put<any>(buildPath(['data-core/Hotel/updateHotel/' + id]), hotel).pipe(
            tap((response: any) => {
                this._hotel.next(response);
            })
        );
    }

    /**
     * Method to gen invoices
     */
    async getInvoicesSatWs(dateEnd, dateInit, income?, pay?, payroll?, discharge?, t?): Promise<any> {
        let type = '';
        if (income) {
            type = type + '&tipo=I';
        }

        if (pay) {
            type = type + '&tipo=P';
        }

        if (payroll) {
            type = type + '&tipo=N';
        }

        if (discharge) {
            type = type + '&tipo=E';
        }

        if (t) {
            type = type + '&tipo=T';
        }

        return await firstValueFrom(this._http.get<any>
        (buildPath(
            ['data-core/Invoice/getInvoicesFiltered' + '?fin=' + dateEnd.toISOString().split('T')[0] + ' 00:00:00.000Z' + '&inicio=' + dateInit.toISOString().split('T')[0] + ' 00:00:00.000Z' + type])));
    }

    /**
     * Method to get invoice relation
     */
    async getInvoicesSatWsRelation(id: number): Promise<any> {
        return await firstValueFrom(this._http.get<any>
        (buildPath(
            ['data-core/RelationsInvoice/getRelationsInvoice/' + id])));
    }

    /**
     * Method to get invoice
     */
    async getInvoicesbyUUID(uuid: string): Promise<any> {
        return await firstValueFrom(this._http.get<any>(buildPath(['data-core/Invoice/getInvoice/fromSATWSby/' + uuid])));
    }

    /**
     * Method to bound hotel to company
     */
    async boundToCompany(companyId: number): Promise<any> {
        return await firstValueFrom(this._http.post<any>(buildPath(['data-core/HotelCompany/boundToCompany/' + companyId]), ''));
    }

    /**
     * Method to bound hotel to company by Master
     */
    async boundToCompanyMaster(companyId: number, hotelId: any): Promise<any> {
        return await firstValueFrom(this._http.post<any>(buildPath(['data-core/HotelCompany/Master/boundToCompany/' + hotelId + '/' + companyId]), ''));
    }

    /**
     * Method to bound company to sub company
     */
    async boundToSubCompany(subCompanyId: number | string, companyId: number | string): Promise<any> {
        return await firstValueFrom(this._http.post<any>(buildPath(['data-core/HotelCompanyCompany/Hotel/createSubProveedor', companyId.toString(), subCompanyId.toString()]), ''));
    }

    /**
     * Method to bound company to sub company by Master
     */
    async boundToSubCompanyMaster(subCompanyId: number | string, companyId: number | string, hotelId: number | string): Promise<any> {
        return await firstValueFrom(this._http.post<any>(buildPath(['data-core/HotelCompanyCompany/Master/createSubProveedor', hotelId.toString(), companyId.toString(), subCompanyId.toString()]), ''));
    }

    /**
     * Method to get Evidence by contract_imss_registry_id
     * @param contractImssRegistryId
     */
    getEvidencesPlusPercentages(contractImssRegistryId: string): Observable<EvidenceMonthResponse[]> {
        const url: string = buildPath(['data-core/evidences/all-by-contract-imss-registry', contractImssRegistryId]);
        return this._http.get<ApiResponse<EvidenceMonthResponse[]>>(url)
            .pipe(map((response: ApiResponse<EvidenceMonthResponse[]>): EvidenceMonthResponse[] => response.data));
    }

    getEvidenceConfig(contractImssRegistryId: string): Observable<EvidenceConfigResponse> {
        const url: string = buildPath(['data-core/evidences/config/by-contract-imss-registry', contractImssRegistryId]);
        return this._http.get<ApiResponse<EvidenceConfigResponse>>(url)
            .pipe(map((response: ApiResponse<EvidenceConfigResponse>): EvidenceConfigResponse => response.data));
    }

    /**
     *
     * @param id
     * @param type
     */
    getFILECFDI(id, type): Observable<any> {
        const headers = new HttpHeaders({
            'x-api-key': environment.apiKeyEDOFAC,
        });
        const options = {headers: headers};
        return this._http.get<any>('https://backend.prod.morales.taxsat.com/cfdi/invoices/' + id + '/' + type,
            options);
    }

    /**
     *
     * @param id
     * @param type
     */
    getPayments(id): Observable<any> {
        const headers = new HttpHeaders({
            'x-api-key': environment.apiKeyEDOFAC,
        });
        const options = {headers: headers};
        return this._http.get<any>('https://backend.prod.morales.taxsat.com/cfdi/payments/' + id,
            options);
    }

    /**
     *
     * @param id
     * @param type
     */
    getBatch(id): Observable<any> {
        const headers = new HttpHeaders({
            'x-api-key': environment.apiKeyEDOFAC,
        });
        const options = {headers: headers};
        return this._http.get<any>('https://backend.prod.morales.taxsat.com/cfdi/batch/' + id,
            options);
    }

    /***
     * Method to update CFDI
     * @param id
     */
    updateCFDI(id): Observable<any> {
        return this._http.put<any>(buildPath(
            ['data-core/Invoice/updateInvoiceBy/' + id]), {});
    }

    /**
     * Method to create credential SATWS
     *
     * @param offset
     * @param pageNumber
     * @param pageSize
     */
    createCredentials(password: string): Observable<any> {
        return this._http.post<any>(buildPath(
            ['data-core/Hotel/satws/ciec/createCredentials/' + password.trim()]), {});
    }

    toggleContracteeModule(contracteeModule: ContracteeModule): Observable<HotelDTO> {
        const url: string = buildPath(['data-core/Hotel/master/module']);
        return this._http.patch<ApiResponse<HotelDTO>>(url, contracteeModule).pipe(
            map((response: ApiResponse<HotelDTO>): HotelDTO => response.data)
        );
    }
}
